import {
  cartDrawerCloseGeneration,
  requestCartDrawerOpen,
} from '../utils/util-functions';

const initShippingState = () => {
  const node = document.querySelector('[data-shipping-text]');
  if (!node) return null;
  const state = parseInt(node.dataset.shippingState, 10);
  return Number.isNaN(state) ? null : state;
};

// Capture initial state at load so the first cart update can detect a threshold crossing.
// Starts as null when no shipping element exists in the DOM, which prevents false-positive analytics events.
let _prevShippingState = initShippingState();

const updateShippingProgress = (container, newNode) => {
  if (!container || !newNode) return;

  const message = container.querySelector('[data-shipping-message]');
  const newMessage = newNode.querySelector('[data-shipping-message]');
  if (message && newMessage) {
    message.innerHTML = newMessage.innerHTML;
  } else {
    container.innerHTML = newNode.innerHTML;
  }

  const barContainer = container.querySelector('[data-bar-container]');
  const newBarContainer = newNode.querySelector('[data-bar-container]');
  if (barContainer && newBarContainer) {
    const isHidden = newBarContainer.classList.contains('hidden');
    barContainer.classList.toggle('hidden', isHidden);

    const fill = barContainer.querySelector('[data-bar-fill]');
    const newFill = newBarContainer.querySelector('[data-bar-fill]');
    if (fill && newFill) {
      fill.style.width = newFill.style.width;
    }
  }

  ['shippingState', 'freeShippingThreshold', 'showShippingText', 'freeShippingAmount', 'cartTotalPrice'].forEach((attr) => {
    if (attr in newNode.dataset) {
      container.dataset[attr] = newNode.dataset[attr];
    }
  });
};

const trackFreeShippingThreshold = (targetNode) => {
  const node = targetNode || document.querySelector('[data-shipping-text]');
  if (!node) return;

  const state = parseInt(node.dataset.shippingState, 10);
  if (Number.isNaN(state)) return;
  const crossed = state === 5 && _prevShippingState !== null && _prevShippingState !== 5;

  if (crossed) {
    const cartTotal = parseInt(node.dataset.cartTotalPrice, 10);
    if (Number.isNaN(cartTotal)) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'free_shipping_reached',
      cart_value: cartTotal / 100,
      currency: window.Shopify?.currency?.active || 'EUR',
    });
  }

  _prevShippingState = state;
};

/**
 * Add item to cart based on variant ID
 * @param {Array} items
 */
const addToCart = async (items) => {
  // Input validation
  if (!items || !Array.isArray(items) || items.length === 0) {
    throw new Error('Items must be a non-empty array');
  }

  // Validate each item has required properties
  for (const item of items) {
    if (!item.id || !item.quantity || typeof item.quantity !== 'number') {
      throw new Error('Each item must have id and numeric quantity');
    }
  }

  const formData = {
    items,
    sections: 'cart-drawer,main-cart',
  };
  try {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const parsedResponse = await response.json();

    /* TODO: Add learnq tracking */
    // window._learnq.push(['track', 'Added to Cart', this.item]);

    if (parsedResponse.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        parsedResponse.sections;
      // update cart
      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    }

    if (!response.ok) {
      try {
        const refetch = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sections: 'cart-drawer,main-cart' }),
        });
        const refetchData = await refetch.json();
        if (refetchData.sections) {
          const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } = refetchData.sections;
          updateCartDrawerContent(cartDrawer);
          updateCartMainContent(mainCart);
        }
      } catch (refetchError) {
        console.error('Refetch failed:', refetchError);
      }

      document.dispatchEvent(new CustomEvent('cart:error', {
        detail: parsedResponse.description || parsedResponse.message || 'An error occurred',
      }));
      return null;
    }

    return parsedResponse;
  } catch (error) {
    console.error('Error:', error);
    document.dispatchEvent(new CustomEvent('cart:error', {
      detail: 'An error occurred while adding to cart',
    }));
    return null;
  }
};

/**
 * Update multiple items in cart
 * @param {object} items - { item_key: quantity }
 */
const updateCart = async (items) => {
  // Input validation
  if (!items || typeof items !== 'object' || Object.keys(items).length === 0) {
    throw new Error('Items must be a non-empty object');
  }

  // Validate each quantity is a number
  for (const [key, quantity] of Object.entries(items)) {
    if (typeof quantity !== 'number' || quantity < 0) {
      throw new Error(
        `Invalid quantity for item ${key}: must be a non-negative number`
      );
    }
  }

  try {
    const formData = {
      updates: items,
      sections: 'cart-drawer,main-cart',
    };
    const response = await fetch(
      window.Shopify.routes.root + 'cart/update.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart update request failed: ${response.status} ${response.statusText}`
      );
    }

    const parsedResponse = await response.json();

    if (parsedResponse.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        parsedResponse.sections;
      // update cart

      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    } else {
      // process the error and show it to the user
      const event = new CustomEvent('cart:error', {
        detail: parsedResponse.description || parsedResponse.message || 'An error occurred',
      });
      document.dispatchEvent(event);
    }
  } catch (error) {
    console.error('Error updating cart:', error);
    throw error;
  }
};

/**
 * Get cart content when we need the cart content without adding, updating or deleting items
 */
const getCart = async () => {
  try {
    // Snapshot before the fetch so a user close during it suppresses the reopen.
    const closeGenerationBefore = cartDrawerCloseGeneration();
    const response = await fetch(
      window.Shopify.routes.root +
        '?sections=header,cart-drawer,main-cart,footer',
      {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Get cart request failed: ${response.status} ${response.statusText}`
      );
    }

    const parsedResponse = await response.json();

    const { 'cart-drawer': cartDrawer } = parsedResponse;
    updateCartDrawerContent(cartDrawer);

    requestCartDrawerOpen(closeGenerationBefore);
  } catch (error) {
    console.error('Error:', error);
    document.dispatchEvent(new CustomEvent('cart:error', {
      detail: 'An error occurred while loading the cart',
    }));
  }
};

/**
 * Change cart item
 * @param {string} key - can either be line item key or variant ID
 * @param {number} quantity
 * @param {object} [properties] - optional line item properties
 * @param {function} [callback]
 */
const changeItem = async ({ key, quantity, properties, callback }) => {
  // Input validation
  if (!key || typeof key !== 'string') {
    throw new Error('Key must be a non-empty string');
  }

  if (typeof quantity !== 'number' || quantity < 0) {
    throw new Error('Quantity must be a non-negative number');
  }

  if (callback && typeof callback !== 'function') {
    throw new Error('Callback must be a function');
  }

  try {
    const formData = {
      id: key,
      quantity,
      properties,
      sections: 'cart-drawer,main-cart',
    };

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(
        `Change cart item request failed: ${response.status} ${response.statusText}`
      );
    }

    const cart = await response.json();

    if (cart.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        cart.sections;

      // update cart count
      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    } else {
      // process the error and show it to the user
      const event = new CustomEvent('cart:error', {
        detail: cart.description || cart.message || 'An error occurred',
      });
      document.dispatchEvent(event);
    }

    if (callback) {
      callback(cart);
    }

    return cart;
  } catch (error) {
    console.error('Error changing cart item:', error);
    throw error;
  }
};

// --- Focus preservation across cart-drawer re-renders (WCAG 2.4.3) ----------
// The drawer content is fully re-rendered on every mutation (see
// updateCartDrawerContent). Replacing innerHTML destroys whatever control had
// focus, so the browser drops focus to <body> while the dialog stays open,
// stranding keyboard/screen-reader users in the background content. These
// helpers capture the focused control before the swap and restore it after.

const cssEscape = (value) =>
  window.CSS && CSS.escape
    ? CSS.escape(value)
    : String(value).replace(/["\\]/g, '\\$&');

// `[data-cart-item="…"]` selector prefix that scopes a control to its line item.
const itemScope = (item) =>
  `[data-cart-item="${cssEscape(item.getAttribute('data-cart-item'))}"]`;

// Returns a selector that re-identifies the focused control after the drawer
// content is replaced, or null when it can't be re-identified (the caller then
// falls back to the dialog container).
const drawerFocusSelector = (el) => {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return null;

  const quantityButton = el.getAttribute('data-quantity-button');
  const itemKey = el.getAttribute('data-item-key');
  if (quantityButton !== null && itemKey !== null) {
    return `[data-quantity-button="${cssEscape(
      quantityButton
    )}"][data-item-key="${cssEscape(itemKey)}"]`;
  }

  const removeKey = el.getAttribute('data-remove-item');
  if (removeKey !== null) return `[data-remove-item="${cssEscape(removeKey)}"]`;

  // The gift-wrapping button carries no per-item value, so it is ambiguous
  // across every wrap-eligible line item; scope the selector to the line item so
  // focus never restores onto a different item's button.
  if (el.hasAttribute('data-gift-wrapping-button')) {
    const item = el.closest('[data-cart-item]');
    return item ? `${itemScope(item)} [data-gift-wrapping-button]` : null;
  }

  // A stable id is the most precise anchor (e.g. the drawer title link).
  if (el.id) return `#${cssEscape(el.id)}`;

  // Line items for the same product share an href, so scope links to their line
  // item. Links outside a line item are returned unscoped; the two cart-url links
  // (title + redirect) both carry ids and are handled by the id branch above, so
  // the only unscoped links have unique hrefs (e.g. the checkout link).
  const href = el.getAttribute('href');
  if (el.tagName === 'A' && href) {
    const item = el.closest('[data-cart-item]');
    return item
      ? `${itemScope(item)} a[href="${cssEscape(href)}"]`
      : `a[href="${cssEscape(href)}"]`;
  }

  return null;
};

const isFocusable = (el) =>
  el && !el.disabled && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null;

// Restore focus into a re-rendered region. `selector` is the focused control's
// pre-render identity (may be null); falls back to focusing the container.
const restoreFocusInto = (container, selector) => {
  const target = selector && container.querySelector(selector);
  if (isFocusable(target)) {
    target.focus({ preventScroll: true });
    return;
  }
  if (!container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
  }
  container.focus({ preventScroll: true });
};

// Preserve focus across an innerHTML swap of `container`: capture the focused
// control before `mutate()`, then restore it after — to the same control where
// it can be re-identified, otherwise to `container`. `active` gates the whole
// thing (the drawer only preserves focus while open). `trapWhenActive` re-anchors
// focus into `container` even when focus was NOT inside it at swap time: on the
// modal drawer, focus that had escaped the content — e.g. dropped to <body> when
// an add button disabled itself, or moved to the adjacent upsell — should return
// to the dialog. Left false, focus is restored only when it was genuinely inside
// `container`, so the non-modal /cart page never steals focus. WCAG 2.4.3.
const preserveFocusAcross = (
  container,
  mutate,
  { active = true, trapWhenActive = false } = {}
) => {
  const hadFocusInside =
    active && container.contains(document.activeElement);
  const selector = hadFocusInside
    ? drawerFocusSelector(document.activeElement)
    : null;

  mutate();

  if (active && (trapWhenActive || hadFocusInside)) {
    restoreFocusInto(container, selector);
  }
};

const updateCartDrawerContent = (cartDrawer) => {
  // Input validation
  if (!cartDrawer || typeof cartDrawer !== 'string') {
    console.error('Invalid cart drawer HTML provided');
    return;
  }

  // Parse the new cart drawer HTML
  const parser = new DOMParser();
  const newCartDrawerHTML = parser.parseFromString(cartDrawer, 'text/html');
  const tempCartDrawerElement = newCartDrawerHTML.querySelector(
    '[data-component="cartDrawer"]'
  );

  if (!tempCartDrawerElement) {
    console.error('Cart drawer element not found in provided HTML');
    return;
  }

  const cartDrawerElement = document.querySelector(
    '[data-component="cartDrawer"]'
  );

  if (!cartDrawerElement) {
    console.error('Cart drawer element not found in DOM');
    return;
  }

  // Update cart count
  const cartDrawerCount = document.querySelectorAll('[data-cart-count]');
  if (cartDrawerCount && cartDrawerCount.length > 0) {
    const rawNewCartCount = tempCartDrawerElement.dataset.cartDrawerCountNumber;

    const newCartCount = Number(rawNewCartCount) || 0;

    cartDrawerCount.forEach((item) => {
      item.textContent = newCartCount;
      if (newCartCount > 0) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  // Update cart content
  const cartDrawerContent = cartDrawerElement.querySelector(
    '[data-cart-drawer-content]'
  );
  const tempCartDrawerContent = tempCartDrawerElement.querySelector(
    '[data-cart-drawer-content]'
  );

  if (cartDrawerContent && tempCartDrawerContent) {
    // Preserve keyboard/SR focus across the re-render while the drawer is open,
    // so the user isn't dropped into the background content (WCAG 2.4.3). The
    // drawer is modal (trapWhenActive), so if focus had escaped the content —
    // e.g. to <body> when an add button disabled itself, or to the upsell — pull
    // it back into the dialog.
    preserveFocusAcross(
      cartDrawerContent,
      () => {
        cartDrawerContent.innerHTML = tempCartDrawerContent.innerHTML;
      },
      {
        active: cartDrawerElement.getAttribute('aria-expanded') === 'true',
        trapWhenActive: true,
      }
    );
  }

  // update cart footer
  const cartDrawerFooter = cartDrawerElement.querySelector(
    '[data-cart-drawer-footer]'
  );
  const tempCartDrawerFooter = tempCartDrawerElement.querySelector(
    '[data-cart-drawer-footer]'
  );
  if (cartDrawerFooter && tempCartDrawerFooter) {
    cartDrawerFooter.innerHTML = tempCartDrawerFooter.innerHTML;
  }

  // update shipping progress
  const shippingNode = cartDrawerElement.querySelector('[data-shipping-text]');
  updateShippingProgress(
    shippingNode,
    tempCartDrawerElement.querySelector('[data-shipping-text]')
  );

  trackFreeShippingThreshold(shippingNode);

  // Emit custom event to reattach event listeners if the current page is not /cart
  if (window.location.pathname !== '/cart') {
    const event = new CustomEvent('cart:update');
    document.dispatchEvent(event);
  }
};

const updateCartMainContent = (mainCart) => {
  // Input validation
  if (!mainCart || typeof mainCart !== 'string') {
    console.error('Invalid main cart HTML provided');
    return;
  }

  // Parse the new main cart HTML
  const parser = new DOMParser();
  const newCartMainHTML = parser.parseFromString(mainCart, 'text/html');
  const tempCartMainElement = newCartMainHTML.querySelector(
    '[data-component="cartMain"]'
  );

  if (!tempCartMainElement) {
    console.error('Cart main element not found in provided HTML');
    return;
  }

  const cartMainElement = document.querySelector('[data-component="cartMain"]');

  if (!cartMainElement) return;

  // Check if the new cart has items or is empty
  const tempCartMainContent = tempCartMainElement.querySelector(
    '[data-cart-main-content]'
  );

  if (!tempCartMainContent) {
    // Cart is empty - replace entire cartMain content. Preserve focus (WCAG
    // 2.4.3) so removing the last item lands on the cart region, not <body>.
    preserveFocusAcross(cartMainElement, () => {
      cartMainElement.innerHTML = tempCartMainElement.innerHTML;
    });
  } else {
    // Cart has items - update specific sections for performance
    const cartMainContent = cartMainElement.querySelector(
      '[data-cart-main-content]'
    );

    if (cartMainContent) {
      // Preserve focus across the re-render (WCAG 2.4.3): restore to the same
      // control, or the cart region when it's gone. Non-modal — focus outside
      // the cart is left untouched.
      preserveFocusAcross(cartMainContent, () => {
        cartMainContent.innerHTML = tempCartMainContent.innerHTML;
      });
    }

    // update shipping text
    updateShippingProgress(
      cartMainElement.querySelector('[data-shipping-text]'),
      tempCartMainElement.querySelector('[data-shipping-text]')
    );
  }

  // Emit custom event to reattach event listeners
  const event = new CustomEvent('main-cart:update');
  document.dispatchEvent(event);
};

export { addToCart, updateCart, changeItem, getCart };
